/**
 * Token Refresh Service
 * Handles silent background token refresh with request queueing
 * Prevents multiple simultaneous refresh attempts and queues requests during refresh
 */

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
const requestQueue: Array<() => void> = [];

/**
 * Queue a request callback to be executed after token refresh completes
 */
export const queueRequest = (callback: () => void) => {
  requestQueue.push(callback);
};

/**
 * Execute all queued requests
 */
const executeQueuedRequests = () => {
  const callbacks = [...requestQueue];
  requestQueue.length = 0;
  callbacks.forEach((callback) => callback());
};

/**
 * Refresh the access token using the refresh token
 * Only one refresh can happen at a time; others wait for the result
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  // If refresh already in progress, wait for it
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('[tokenRefresh] No refresh token available');
        return false;
      }

      // Use relative endpoint in web browser, or electron server url if present
      let refreshUrl = '/api/auth/refresh';
      if (window.electronAPI?.isElectron) {
        const serverUrl = await window.electronAPI.getServerUrl();
        if (serverUrl) {
          refreshUrl = `${serverUrl}/api/auth/refresh`;
        }
      }

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        console.warn('[tokenRefresh] Token refresh rejected', response.status);
        // Clear tokens only if explicitly unauthorized or forbidden
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
        return false;
      }

      const data = await response.json();

      // Store new tokens
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        console.debug('[tokenRefresh] Access token refreshed successfully');

        // If new refresh token provided, update it
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('[tokenRefresh] Token refresh network error:', error);
      // Do not clear tokens on temporary network glitches
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
      executeQueuedRequests();
    }
  })();

  return refreshPromise;
};

/**
 * Check if currently refreshing
 */
export const isTokenRefreshing = (): boolean => isRefreshing;

/**
 * Get pending request count
 */
export const getPendingRequestCount = (): number => requestQueue.length;
